/**
 * Shared custom ("bring your own AI") provider management UI.
 *
 * One component, two surfaces: the project settings modal manages
 * `/projects/:id/ai-provider` and the account settings panel manages
 * `/account/ai-provider` — the routes share bodies and responses, so the only
 * per-surface inputs are the `basePath`, a `data-mol-id` prefix (so the two
 * surfaces never collide in tests), and the `onChanged` callback the host uses
 * to refresh the models catalog after a save/delete.
 *
 * Server gates render as short friendly lines: a project 403 means "not an
 * admin"; the account guest 403 carries `requiresSignup` and reads as a
 * sign-up prompt. Keys are write-only — reads always serve the masked display.
 *
 * @module
 */

import { type JSX, useEffect, useState } from 'react'

import { useAIModels, useHttpClient, useTranslation } from '@molecule/app-react'
import { Button, Dropdown, Modal, Switch } from '@molecule/app-ui-react'

import { confirmDialog } from './AppDialogs.js'

/** Supported wire formats for a custom ("bring your own AI") provider. */
type AiProviderKind = 'openai' | 'anthropic' | 'google'

/** One model row a provider declares — mirrors the API's `CustomModelEntry`. */
interface CustomModelEntry {
  id: string
  label?: string
  contextWindow?: number
  maxOutputTokens?: number
  /** Extra provider-native request-body params merged into every call. */
  params?: Record<string, unknown>
}

/** Masked provider row returned by `GET <basePath>`. */
interface AiProviderDisplay {
  name: string
  kind: AiProviderKind
  baseUrl: string | null
  maskedKey: string
  models: CustomModelEntry[]
  enabled: boolean
  updatedAt: string
}

/** Provider name slug rule — mirrors the server's validation. */
const AI_PROVIDER_NAME_RE = /^[a-z0-9][a-z0-9-]{0,31}$/

/**
 * Parses the models textarea — one model per line, `id | label | contextWindow
 * | maxOutputTokens` with everything after the id optional.
 *
 * @param text - The raw textarea value.
 * @returns The declared model entries.
 */
function parseModelsText(text: string): CustomModelEntry[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [id = '', label = '', contextWindow = '', maxOutputTokens = ''] = line
        .split('|')
        .map((part) => part.trim())
      const entry: CustomModelEntry = { id }
      if (label) entry.label = label
      const ctx = Number(contextWindow)
      if (contextWindow && Number.isFinite(ctx) && ctx > 0) entry.contextWindow = Math.floor(ctx)
      const out = Number(maxOutputTokens)
      if (maxOutputTokens && Number.isFinite(out) && out > 0) {
        entry.maxOutputTokens = Math.floor(out)
      }
      return entry
    })
}

/**
 * Serializes stored model entries back into the textarea format so an edit
 * round-trips every declared field.
 *
 * @param models - The stored model entries.
 * @returns The textarea value.
 */
function serializeModels(models: CustomModelEntry[]): string {
  return models
    .map((m) => {
      const parts = [m.id, m.label ?? '', m.contextWindow?.toString() ?? '']
      if (m.maxOutputTokens != null) parts.push(String(m.maxOutputTokens))
      // Drop empty trailing columns so a bare id stays a bare id.
      while (parts.length > 1 && parts[parts.length - 1] === '') parts.pop()
      return parts.join(' | ')
    })
    .join('\n')
}

/**
 * Normalize a user-entered base URL: strip trailing slashes and a stray
 * trailing `/v1`. The provider bonds append their own path
 * (`/v1/chat/completions`, `/v1/messages`), so a base URL that already ends in
 * `/v1` doubles it (`/v1/v1/...` → 404). Users naturally think of their
 * endpoint as `.../v1`, so accept that and correct it rather than 404. Only an
 * exact trailing `/v1` is stripped — `/v1beta` (Google) is left intact.
 *
 * @param raw - The base URL as entered.
 * @returns The normalized base URL (may be empty).
 */
function normalizeBaseUrl(raw: string): string {
  return raw.trim().replace(/\/+$/, '').replace(/\/v1$/i, '').replace(/\/+$/, '')
}

/**
 * Parse the "Extra request parameters" JSON field. Empty is valid (no params).
 * A parsed value is valid only when it's a plain JSON object.
 *
 * @param text - The raw textarea value.
 * @returns `ok` with the object (or `undefined` when empty), or an error label.
 */
function parseParamsText(text: string): {
  ok: boolean
  value?: Record<string, unknown>
  error?: string
} {
  const trimmed = text.trim()
  if (!trimmed) return { ok: true }
  let parsed: unknown
  try {
    parsed = JSON.parse(trimmed)
  } catch (_error) {
    // Malformed JSON is expected user input mid-typing — surfaced inline, not logged.
    return { ok: false, error: 'invalid-json' }
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { ok: false, error: 'not-object' }
  }
  return { ok: true, value: parsed as Record<string, unknown> }
}

/** Extracts the server-provided error message, HTTP status, and signup flag. */
function serverError(err: unknown): {
  message: string | null
  status: number | null
  requiresSignup: boolean
} {
  const e = err as {
    status?: number
    response?: { data?: { error?: string; requiresSignup?: boolean } }
  }
  return {
    message: typeof e?.response?.data?.error === 'string' ? e.response.data.error : null,
    status: typeof e?.status === 'number' ? e.status : null,
    requiresSignup: e?.response?.data?.requiresSignup === true,
  }
}

// The IDE's standard boxed text input (same treatment as the repo-import
// modal and Deploy panel) — theme-token colors for both light and dark.
const textInputStyle = {
  width: '100%',
  height: 32,
  boxSizing: 'border-box',
  fontSize: 13,
  padding: '6px 10px',
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: 6,
  outline: 'none',
  color: 'var(--color-foreground)',
} as const

// Multi-line variant — same treatment, natural height.
const textareaStyle = {
  ...textInputStyle,
  height: 'auto',
  minHeight: 72,
  lineHeight: 1.5,
} as const

const fieldLabelStyle = {
  display: 'block',
  fontSize: 12,
  fontWeight: 500,
  color: 'var(--color-foreground)',
  marginBottom: 4,
} as const

// Bordered rounded group — provider rows and the add/edit form share it.
const groupBoxStyle = {
  border: '1px solid var(--color-border-secondary)',
  borderRadius: 8,
  padding: 12,
} as const

const mutedTextStyle = {
  fontSize: 12,
  color: 'var(--mol-color-text-secondary, #888)',
} as const

// Semantic result/error lines — muted gray for a FAILURE read as "nothing
// happened"; these use the theme's status tokens.
const errorTextStyle = {
  fontSize: 12,
  color: 'var(--color-error, #dc2626)',
} as const

const successTextStyle = {
  fontSize: 12,
  color: 'var(--color-success, #16a34a)',
} as const

/**
 * Invisible helper mounted next to a models-catalog consumer: `useAIModels`
 * fetches only on mount, so after `resetAIModelsCache()` the already-mounted
 * subscribers would sit on an empty cache. Remounting this (via a fresh `key`)
 * re-runs the mount effect, which refetches the catalog and notifies every
 * subscriber of the same scope.
 *
 * @param props - Component props.
 * @param props.projectId - Optional project scope; omit for the account-level
 *   (unscoped) catalog.
 * @returns Nothing — renders no UI.
 */
export function ModelsCatalogRefetch({ projectId }: { projectId?: string }): null {
  useAIModels(projectId)
  return null
}

/** Draft state for the add/edit provider form. */
interface ProviderFormState {
  name: string
  kind: AiProviderKind
  baseUrl: string
  apiKey: string
  modelsText: string
  /** Extra request-body params (JSON), applied to every model of this provider. */
  paramsText: string
  enabled: boolean
  /** Set when editing an existing provider — locks the name, masks the key. */
  editing: AiProviderDisplay | null
}

/** Per-provider connectivity-test state. */
interface TestState {
  running: boolean
  ok?: boolean
  model?: string
  error?: string
}

/**
 * Custom AI provider management — list, add/edit form (PUT), connectivity test
 * (POST …/test), and delete, against the routes under `basePath`. The host
 * renders its own section heading; this component starts with the hint line.
 *
 * @param props - Component props.
 * @param props.basePath - Route base, e.g. `/projects/:id/ai-provider` or
 *   `/account/ai-provider`.
 * @param props.molIdPrefix - Prefix for every `data-mol-id` this component
 *   renders, so multiple surfaces stay distinguishable.
 * @param props.onChanged - Called after a successful save/delete so the host
 *   can refresh the models catalog.
 * @returns The rendered provider manager.
 */
export function AiProviderManager({
  basePath,
  molIdPrefix = 'settings-ai-provider',
  onChanged,
  onUseModel,
  overriddenNames,
}: {
  basePath: string
  molIdPrefix?: string
  onChanged: () => void
  /**
   * When set, each provider row shows a "Use" control that activates one of the
   * provider's models as the chat model, via `custom/<provider>/<model>`: a
   * plain button for a single-model provider, or a dropdown to pick the model
   * when it declares several. Omitted on surfaces with no active chat (the
   * account settings panel), which then show only Test/Edit/Delete.
   */
  onUseModel?: (customModelId: string) => void
  /**
   * Provider names that a HIGHER-precedence scope overrides in the current
   * project's model catalog (a project row shadows a same-named account row —
   * whole-provider, by design). Overridden rows show a warning and hide "Use":
   * their `custom/<name>/<model>` ids don't resolve here, so activating one
   * would silently dispatch to the shadowing provider's config instead.
   */
  overriddenNames?: readonly string[]
}): JSX.Element {
  const { t } = useTranslation()
  const http = useHttpClient()

  const [providers, setProviders] = useState<AiProviderDisplay[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [reloadTick, setReloadTick] = useState(0)
  const [form, setForm] = useState<ProviderFormState | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [tests, setTests] = useState<Record<string, TestState>>({})

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const res = await http.get<{ providers: AiProviderDisplay[] }>(basePath)
        if (!cancelled) {
          setProviders(res.data.providers ?? [])
          setLoadError(null)
        }
      } catch (error) {
        console.error('Failed to load AI providers', error)
        if (!cancelled) {
          const { status, requiresSignup } = serverError(error)
          setLoadError(
            requiresSignup || status === 401
              ? t('workspace.settings.aiProvider.signupRequired', undefined, {
                  defaultValue: 'Sign up to add your own AI providers.',
                })
              : status === 403
                ? t('workspace.settings.aiProvider.forbidden', undefined, {
                    defaultValue: 'Only project admins can manage AI providers.',
                  })
                : t('workspace.settings.aiProvider.loadFailed', undefined, {
                    defaultValue: 'Failed to load AI providers.',
                  }),
          )
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [http, basePath, reloadTick, t])

  const reload = (): void => setReloadTick((n) => n + 1)

  const kindOptions: { value: AiProviderKind; label: string }[] = [
    {
      value: 'openai',
      label: t('workspace.settings.aiProvider.kind.openai', undefined, {
        defaultValue: 'OpenAI-compatible',
      }),
    },
    {
      value: 'anthropic',
      label: t('workspace.settings.aiProvider.kind.anthropic', undefined, {
        defaultValue: 'Anthropic-compatible',
      }),
    },
    {
      value: 'google',
      label: t('workspace.settings.aiProvider.kind.google', undefined, {
        defaultValue: 'Google Gemini-compatible',
      }),
    },
  ]
  const kindLabel = (kind: AiProviderKind): string =>
    kindOptions.find((o) => o.value === kind)?.label ?? kind

  const openAdd = (): void => {
    setSaveError(null)
    setForm({
      name: '',
      kind: 'openai',
      baseUrl: '',
      apiKey: '',
      modelsText: '',
      paramsText: '',
      enabled: true,
      editing: null,
    })
  }

  const openEdit = (provider: AiProviderDisplay): void => {
    setSaveError(null)
    // Extra params are applied per-provider, so surface the first model's params
    // (the form writes the same object to every model on save).
    const existingParams = provider.models.find((m) => m.params)?.params
    setForm({
      name: provider.name,
      kind: provider.kind,
      baseUrl: provider.baseUrl ?? '',
      apiKey: '',
      modelsText: serializeModels(provider.models),
      paramsText: existingParams ? JSON.stringify(existingParams, null, 2) : '',
      enabled: provider.enabled,
      editing: provider,
    })
  }

  const nameValid = form ? AI_PROVIDER_NAME_RE.test(form.name) : false
  const modelsValid = form ? parseModelsText(form.modelsText).every((m) => m.id) : false
  const paramsResult = parseParamsText(form?.paramsText ?? '')
  const canSave =
    form != null &&
    nameValid &&
    modelsValid &&
    paramsResult.ok &&
    form.modelsText.trim().length > 0 &&
    // A new provider needs a key UNLESS it targets a custom base URL — a
    // keyless local/self-hosted endpoint (Ollama, LM Studio) needs none. On
    // edit an empty field keeps the stored key.
    (form.editing != null || form.apiKey.length > 0 || form.baseUrl.trim().length > 0)

  const save = async (): Promise<void> => {
    if (!form || !canSave) return
    setSaving(true)
    setSaveError(null)
    try {
      const baseUrl = normalizeBaseUrl(form.baseUrl)
      const extraParams = parseParamsText(form.paramsText).value
      const models = parseModelsText(form.modelsText).map((m) =>
        extraParams ? { ...m, params: extraParams } : m,
      )
      await http.put(`${basePath}/${form.name}`, {
        kind: form.kind,
        ...(baseUrl ? { baseUrl } : {}),
        // Omitted on edit when left empty — the server keeps the stored key.
        ...(form.apiKey ? { apiKey: form.apiKey } : {}),
        models,
        enabled: form.enabled,
      })
      setForm(null)
      reload()
      onChanged()
    } catch (error) {
      console.error('Failed to save AI provider', error)
      setSaveError(
        serverError(error).message ??
          t('workspace.settings.aiProvider.saveFailed', undefined, {
            defaultValue: 'Failed to save provider.',
          }),
      )
    } finally {
      setSaving(false)
    }
  }

  const remove = async (name: string): Promise<void> => {
    const confirmed = await confirmDialog({
      message: t(
        'workspace.settings.aiProvider.confirmDelete',
        { name },
        { defaultValue: 'Delete "{{name}}"? Its models disappear from the model pickers.' },
      ),
      danger: true,
      molId: 'ai-provider-delete-dialog',
    })
    if (!confirmed) return
    setActionError(null)
    try {
      await http.delete(`${basePath}/${name}`)
      reload()
      onChanged()
    } catch (error) {
      console.error('Failed to delete AI provider', error)
      setActionError(
        serverError(error).message ??
          t('workspace.settings.aiProvider.deleteFailed', undefined, {
            defaultValue: 'Failed to delete provider.',
          }),
      )
    }
  }

  const runTest = async (name: string): Promise<void> => {
    setTests((prev) => ({ ...prev, [name]: { running: true } }))
    try {
      const res = await http.post<{ ok: boolean; model?: string; error?: string }>(
        `${basePath}/${name}/test`,
        {},
      )
      setTests((prev) => ({ ...prev, [name]: { running: false, ...res.data } }))
    } catch (error) {
      console.error('AI provider test failed', error)
      setTests((prev) => ({
        ...prev,
        [name]: {
          running: false,
          ok: false,
          error:
            serverError(error).message ??
            t('workspace.settings.aiProvider.testFailed', undefined, {
              defaultValue: 'Test failed.',
            }),
        },
      }))
    }
  }

  return (
    <div data-mol-id={`${molIdPrefix}-section`}>
      <div style={{ ...mutedTextStyle, marginTop: 2 }}>
        {t('workspace.settings.aiProvider.hint', undefined, {
          defaultValue:
            'Use your own API key. OpenAI-compatible covers OpenRouter, Ollama, Groq and most gateways.',
        })}
      </div>

      {loadError ? (
        <div style={{ ...mutedTextStyle, marginTop: 8 }}>{loadError}</div>
      ) : providers == null ? (
        <div style={{ ...mutedTextStyle, marginTop: 8 }}>
          {t('common.loading', undefined, { defaultValue: 'Loading…' })}
        </div>
      ) : (
        <>
          {providers.length === 0 && form == null && (
            <div style={{ ...mutedTextStyle, marginTop: 8 }}>
              {t('workspace.settings.aiProvider.empty', undefined, {
                defaultValue: 'No providers configured.',
              })}
            </div>
          )}

          {providers.map((provider) => {
            const test = tests[provider.name]
            const overridden = overriddenNames?.includes(provider.name) === true
            return (
              <div
                key={provider.name}
                data-mol-id={`${molIdPrefix}-row-${provider.name}`}
                style={{ ...groupBoxStyle, marginTop: 8 }}
              >
                {/* Identity line, then meta, then result, then a dedicated
                    actions row — the old single name+buttons line wrapped
                    unpredictably in narrow hosts (the account panel). */}
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      fontFamily: 'monospace',
                      color: 'var(--color-foreground)',
                    }}
                  >
                    {provider.name}
                  </span>
                  <span style={mutedTextStyle}>{kindLabel(provider.kind)}</span>
                  {!provider.enabled && (
                    <span style={mutedTextStyle}>
                      {t('workspace.settings.aiProvider.disabled', undefined, {
                        defaultValue: 'off',
                      })}
                    </span>
                  )}
                </div>
                <div style={{ ...mutedTextStyle, marginTop: 2, fontFamily: 'monospace' }}>
                  {provider.baseUrl ? `${provider.baseUrl} · ` : ''}
                  {provider.maskedKey} ·{' '}
                  {t(
                    'workspace.settings.aiProvider.modelCount',
                    { count: provider.models.length },
                    { defaultValue: '{{count}} models' },
                  )}
                </div>
                {overridden && (
                  <div
                    style={{ ...errorTextStyle, marginTop: 4 }}
                    data-mol-id={`${molIdPrefix}-overridden-${provider.name}`}
                  >
                    {t('workspace.settings.aiProvider.overridden', undefined, {
                      defaultValue:
                        'Overridden here: this project has its own provider with the same name, and the project one wins. Edit or delete it above to use this one.',
                    })}
                  </div>
                )}
                {test && !test.running && (
                  <div style={{ ...(test.ok ? successTextStyle : errorTextStyle), marginTop: 4 }}>
                    {test.ok
                      ? t(
                          'workspace.settings.aiProvider.testOk',
                          { model: test.model ?? '' },
                          { defaultValue: 'OK — {{model}} responded.' },
                        )
                      : (test.error ??
                        t('workspace.settings.aiProvider.testFailed', undefined, {
                          defaultValue: 'Test failed.',
                        }))}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  {/* Single model → a plain "Use" button; multiple → a dropdown
                      so the user picks WHICH model to activate. Hidden on rows a
                      project-scoped provider overrides — their ids don't resolve
                      in this project's catalog. */}
                  {onUseModel && !overridden && provider.models.length === 1 && (
                    <Button
                      size="sm"
                      variant="solid"
                      color="primary"
                      disabled={!provider.enabled}
                      onClick={() => onUseModel(`custom/${provider.name}/${provider.models[0].id}`)}
                      data-mol-id={`${molIdPrefix}-use-${provider.name}`}
                    >
                      {t('workspace.settings.aiProvider.use', undefined, { defaultValue: 'Use' })}
                    </Button>
                  )}
                  {onUseModel && !overridden && provider.models.length > 1 && (
                    <Dropdown
                      trigger={
                        <Button
                          size="sm"
                          variant="solid"
                          color="primary"
                          disabled={!provider.enabled}
                          data-mol-id={`${molIdPrefix}-use-${provider.name}`}
                        >
                          {t('workspace.settings.aiProvider.use', undefined, {
                            defaultValue: 'Use',
                          })}{' '}
                          ▾
                        </Button>
                      }
                      items={provider.models.map((m) => ({ value: m.id, label: m.label ?? m.id }))}
                      onSelect={(id) => onUseModel(`custom/${provider.name}/${id}`)}
                      testId={`${molIdPrefix}-use-menu-${provider.name}`}
                    />
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    color="secondary"
                    disabled={test?.running === true}
                    onClick={() => void runTest(provider.name)}
                    data-mol-id={`${molIdPrefix}-test-${provider.name}`}
                  >
                    {test?.running === true
                      ? t('workspace.settings.aiProvider.testing', undefined, {
                          defaultValue: 'Testing…',
                        })
                      : t('workspace.settings.aiProvider.test', undefined, {
                          defaultValue: 'Test',
                        })}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    color="secondary"
                    onClick={() => openEdit(provider)}
                    data-mol-id={`${molIdPrefix}-edit-${provider.name}`}
                  >
                    {t('workspace.settings.aiProvider.edit', undefined, { defaultValue: 'Edit' })}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    color="error"
                    onClick={() => void remove(provider.name)}
                    data-mol-id={`${molIdPrefix}-delete-${provider.name}`}
                  >
                    {t('workspace.settings.aiProvider.delete', undefined, {
                      defaultValue: 'Delete',
                    })}
                  </Button>
                </div>
              </div>
            )
          })}

          {actionError && <div style={{ ...errorTextStyle, marginTop: 6 }}>{actionError}</div>}

          <div style={{ marginTop: 8 }}>
            <Button
              size="sm"
              variant="outline"
              fullWidth
              onClick={openAdd}
              data-mol-id={`${molIdPrefix}-add`}
            >
              {t('workspace.settings.aiProvider.add', undefined, {
                defaultValue: 'Add provider',
              })}
            </Button>
          </div>

          {/* The add/edit form opens in its OWN modal (portaled to body, above
              the host panel/modal) — inline it crammed the narrow account
              panel. All field data-mol-ids are unchanged. */}
          <Modal
            open={form != null}
            onClose={() => setForm(null)}
            size="md"
            title={
              form?.editing
                ? t('workspace.settings.aiProvider.editTitle', undefined, {
                    defaultValue: 'Edit provider',
                  })
                : t('workspace.settings.aiProvider.add', undefined, {
                    defaultValue: 'Add provider',
                  })
            }
            data-mol-id={`${molIdPrefix}-form`}
          >
            {form != null && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                }}
              >
                {/* Name + kind side by side where the width allows; each column
                  keeps a sane minimum so narrow hosts stack them instead. */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: 12,
                  }}
                >
                  <div>
                    <label style={fieldLabelStyle} htmlFor={`${molIdPrefix}-name`}>
                      {t('workspace.settings.aiProvider.name', undefined, { defaultValue: 'Name' })}
                    </label>
                    <input
                      id={`${molIdPrefix}-name`}
                      type="text"
                      value={form.name}
                      disabled={form.editing != null}
                      placeholder="my-provider"
                      data-mol-id={`${molIdPrefix}-name`}
                      aria-label={t('workspace.settings.aiProvider.name', undefined, {
                        defaultValue: 'Name',
                      })}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      style={textInputStyle}
                    />
                    {form.editing == null && form.name.length > 0 && !nameValid && (
                      <div style={{ ...errorTextStyle, marginTop: 4, whiteSpace: 'nowrap' }}>
                        {t('workspace.settings.aiProvider.nameHint', undefined, {
                          defaultValue: 'Lowercase letters, digits and dashes only.',
                        })}
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={fieldLabelStyle} htmlFor={`${molIdPrefix}-kind`}>
                      {t('workspace.settings.aiProvider.kindLabel', undefined, {
                        defaultValue: 'Kind',
                      })}
                    </label>
                    {/* Native boxed select — same treatment as the Deploy panel's
                      form selects, and the same box scale as the inputs here. */}
                    <select
                      id={`${molIdPrefix}-kind`}
                      value={form.kind}
                      onChange={(e) => setForm({ ...form, kind: e.target.value as AiProviderKind })}
                      data-mol-id={`${molIdPrefix}-kind`}
                      aria-label={t('workspace.settings.aiProvider.kindLabel', undefined, {
                        defaultValue: 'Kind',
                      })}
                      style={textInputStyle}
                    >
                      {kindOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label style={fieldLabelStyle} htmlFor={`${molIdPrefix}-base-url`}>
                    {t('workspace.settings.aiProvider.baseUrl', undefined, {
                      defaultValue: 'Base URL (optional)',
                    })}
                  </label>
                  <input
                    id={`${molIdPrefix}-base-url`}
                    type="text"
                    value={form.baseUrl}
                    placeholder="https://openrouter.ai/api"
                    data-mol-id={`${molIdPrefix}-base-url`}
                    aria-label={t('workspace.settings.aiProvider.baseUrl', undefined, {
                      defaultValue: 'Base URL (optional)',
                    })}
                    onChange={(e) => setForm({ ...form, baseUrl: e.target.value })}
                    style={textInputStyle}
                  />
                  <div style={{ ...mutedTextStyle, marginTop: 4 }}>
                    {t('workspace.settings.aiProvider.baseUrlHintPath', undefined, {
                      defaultValue:
                        'Enter the server root only — no /v1 or /chat/completions (the provider appends that).',
                    })}
                  </div>
                  <div style={{ ...mutedTextStyle, marginTop: 2 }}>
                    {t('workspace.settings.aiProvider.baseUrlHint', undefined, {
                      defaultValue:
                        'A local model must be reachable at a public URL — expose it with a tunnel (e.g. ngrok, cloudflared). localhost is not reachable.',
                    })}
                  </div>
                </div>

                <div>
                  <label style={fieldLabelStyle} htmlFor={`${molIdPrefix}-api-key`}>
                    {t('workspace.settings.aiProvider.apiKey', undefined, {
                      defaultValue: 'API key (optional with a custom base URL)',
                    })}
                  </label>
                  <input
                    id={`${molIdPrefix}-api-key`}
                    type="password"
                    value={form.apiKey}
                    placeholder={form.editing?.maskedKey ?? 'sk-…'}
                    data-mol-id={`${molIdPrefix}-api-key`}
                    aria-label={t('workspace.settings.aiProvider.apiKey', undefined, {
                      defaultValue: 'API key (optional with a custom base URL)',
                    })}
                    autoComplete="off"
                    onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
                    style={textInputStyle}
                  />
                  {form.editing != null && (
                    <div style={{ ...mutedTextStyle, marginTop: 4 }}>
                      {t('workspace.settings.aiProvider.apiKeyKeep', undefined, {
                        defaultValue: 'Leave empty to keep the current key.',
                      })}
                    </div>
                  )}
                </div>

                <div>
                  <label style={fieldLabelStyle} htmlFor={`${molIdPrefix}-models`}>
                    {t('workspace.settings.aiProvider.models', undefined, {
                      defaultValue: 'Models',
                    })}
                  </label>
                  <textarea
                    id={`${molIdPrefix}-models`}
                    value={form.modelsText}
                    rows={3}
                    placeholder={'llama-3.3-70b\ngpt-4o | GPT-4o | 128000'}
                    data-mol-id={`${molIdPrefix}-models`}
                    aria-label={t('workspace.settings.aiProvider.models', undefined, {
                      defaultValue: 'Models',
                    })}
                    onChange={(e) => setForm({ ...form, modelsText: e.target.value })}
                    style={{ ...textareaStyle, fontFamily: 'monospace', resize: 'vertical' }}
                  />
                  <div style={{ ...mutedTextStyle, marginTop: 4 }}>
                    {t('workspace.settings.aiProvider.modelsHint', undefined, {
                      defaultValue: 'One per line: id | label | context window.',
                    })}
                  </div>
                </div>

                <div>
                  <label style={fieldLabelStyle} htmlFor={`${molIdPrefix}-params`}>
                    {t('workspace.settings.aiProvider.params', undefined, {
                      defaultValue: 'Extra request parameters (optional, JSON)',
                    })}
                  </label>
                  <textarea
                    id={`${molIdPrefix}-params`}
                    value={form.paramsText}
                    rows={3}
                    placeholder={'e.g. { "reasoning_effort": "high", "enable_thinking": true }'}
                    data-mol-id={`${molIdPrefix}-params`}
                    aria-label={t('workspace.settings.aiProvider.params', undefined, {
                      defaultValue: 'Extra request parameters (optional, JSON)',
                    })}
                    onChange={(e) => setForm({ ...form, paramsText: e.target.value })}
                    style={{ ...textareaStyle, fontFamily: 'monospace', resize: 'vertical' }}
                  />
                  <div style={{ ...mutedTextStyle, marginTop: 4 }}>
                    {t('workspace.settings.aiProvider.paramsHint', undefined, {
                      defaultValue:
                        'Merged into every request to this provider — e.g. reasoning_effort, enable_thinking, top_p. Applies to all models above.',
                    })}
                  </div>
                  {form.paramsText.trim().length > 0 && !paramsResult.ok && (
                    <div style={{ ...errorTextStyle, marginTop: 4 }}>
                      {paramsResult.error === 'not-object'
                        ? t('workspace.settings.aiProvider.paramsNotObject', undefined, {
                            defaultValue:
                              'Must be a JSON object, e.g. {"reasoning_effort":"high"}.',
                          })
                        : t('workspace.settings.aiProvider.paramsInvalid', undefined, {
                            defaultValue: 'Invalid JSON.',
                          })}
                    </div>
                  )}
                </div>

                {saveError && <div style={errorTextStyle}>{saveError}</div>}

                {/* One row: Enabled toggle left, actions right (Save rightmost;
                    Cancel gray beside it). */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Switch
                      checked={form.enabled}
                      data-mol-id={`${molIdPrefix}-enabled`}
                      aria-label={t('workspace.settings.aiProvider.enabled', undefined, {
                        defaultValue: 'Enabled',
                      })}
                      onChange={(e) =>
                        setForm({ ...form, enabled: (e.target as HTMLInputElement).checked })
                      }
                    />
                    <span style={mutedTextStyle}>
                      {t('workspace.settings.aiProvider.enabled', undefined, {
                        defaultValue: 'Enabled',
                      })}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setForm(null)}
                      data-mol-id={`${molIdPrefix}-cancel`}
                    >
                      {t('common.cancel', undefined, { defaultValue: 'Cancel' })}
                    </Button>
                    <Button
                      size="sm"
                      variant="solid"
                      disabled={!canSave || saving}
                      onClick={() => void save()}
                      data-mol-id={`${molIdPrefix}-save`}
                    >
                      {saving
                        ? t('common.saving', undefined, { defaultValue: 'Saving…' })
                        : t('common.save', undefined, { defaultValue: 'Save' })}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </Modal>
        </>
      )}
    </div>
  )
}
